<template>
  <div class="google-home">
    <!-- Hero Section -->
    <div class="hero">
      <h1 class="hero-title">{{ $t('hero.title') }}</h1>
      <p class="hero-subtitle">{{ $t('hero.subtitle') }}</p>
      <div class="hero-actions">
        <router-link to="/how-cashback-works" class="btn btn-success btn-lg">How cash back works</router-link>
        <router-link to="/cashback-guide" class="btn btn-outline-success btn-lg ml-2">Read the guide</router-link>
      </div>
    </div>

    <section class="trust-strip" aria-labelledby="cashback-basics-title">
      <div class="trust-content">
        <p class="section-kicker">TRANSPARENT SHOPPING REWARDS</p>
        <h2 id="cashback-basics-title">Know when a link earns cash back—and when it does not</h2>
        <p>CashBoomerang separates eligible cash-back offers from ordinary affiliate shopping links. Only an offer explicitly marked <strong>Cash Back Eligible</strong> can create a user reward. Retailers validate purchases before rewards become available.</p>
        <div class="trust-points">
          <div><strong>Clear eligibility</strong><span>Rates and exclusions belong with each offer.</span></div>
          <div><strong>Visible status</strong><span>Tracked rewards move from pending to available after approval.</span></div>
          <div><strong>Honest disclosure</strong><span>Amazon links are affiliate deals, not user cash-back offers.</span></div>
        </div>
      </div>
    </section>

    <!-- Main Content -->
    <div class="content-wrapper">
      <div class="content-heading">
        <div>
          <p class="section-kicker mb-2">CURATED DEALS</p>
          <h2>Browse current shopping picks</h2>
        </div>
        <p class="affiliate-note">We may earn a commission from links in this section. Amazon purchases do not earn CashBoomerang user cash back.</p>
      </div>
      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
      </div>
      <b-alert v-else-if="errorMessage" show variant="danger" class="text-center">
        {{ errorMessage }}
      </b-alert>
      <b-row v-else-if="filteredProducts.length > 0">
        <b-col md="6" lg="3" class="mb-4" v-for="product in filteredProducts" :key="product.id">
          <div class="card h-100 shadow-sm border-0 product-card" @click="goToDetail(product)">
            <div class="position-relative overflow-hidden product-image-wrapper">
              <img :src="getProxiedImage(product.imagen)" :alt="product.titulo" class="product-image" @error="handleImageError">
            </div>
            <div class="product-info">
              <span class="product-category bg-light text-muted px-2 py-1 rounded small mb-2 d-inline-block">{{ product.category }}</span>
              <span v-if="isCashbackEligible(product)" class="eligibility-badge eligible">Cash Back Eligible</span>
              <span v-else class="eligibility-badge affiliate">Affiliate Deal</span>
              <h2 class="product-name h6 font-weight-bold text-dark mb-2" style="display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; line-height: 1.4; max-height: 2.8em;">{{ product.titulo }}</h2>
              
              <p v-if="product.ai_one_liner" class="product-description text-brand small mb-3">
                <b-icon-chat-quote-fill scale="0.8" class="mr-1"></b-icon-chat-quote-fill>
                {{ product.ai_one_liner }}
              </p>
              <p v-else-if="product.ai_why_love" class="product-description text-brand small mb-3">
                {{ Array.isArray(product.ai_why_love) ? product.ai_why_love[0].substring(0, 60) : product.ai_why_love.substring(0, 60) }}...
              </p>

              <div class="product-footer mt-auto d-flex justify-content-between align-items-center">
                <span class="product-price font-weight-bold text-dark">{{ product.precio }}</span>
                <b-button size="sm" variant="success" class="buy-button" @click.stop.prevent="handleBuy(product)">
                  View Deal
                </b-button>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
      <div v-else class="empty-state text-center py-5">
        <b-icon-inbox width="64" height="64" variant="secondary" class="mb-3 opacity-50"></b-icon-inbox>
        <p class="empty-message h5 text-muted">{{ $t('product.no_products') }}</p>
        <p class="empty-hint text-secondary">{{ $t('product.add_keywords') }}</p>
      </div>
    </div>
    <!-- Product Detail Modal -->
    <b-modal id="product-modal" size="lg" hide-footer centered body-class="p-0">
      <template #modal-header="{ close }">
        <h5 class="modal-title w-100 text-truncate pr-4">{{ selectedProduct ? selectedProduct.titulo : '' }}</h5>
        <b-button size="sm" variant="link" class="close-btn" @click="close">
          <b-icon-x scale="1.5"></b-icon-x>
        </b-button>
      </template>
      
      <div v-if="selectedProduct" class="product-detail-content">
        <div class="row no-gutters">
          <!-- Left: Image -->
          <div class="col-md-5 bg-light d-flex align-items-center justify-content-center p-4">
             <img :src="getProxiedImage(selectedProduct.imagen)" :alt="selectedProduct.titulo" class="img-fluid rounded shadow-sm detail-image" @error="handleImageError">
          </div>
          
          <!-- Right: Info -->
          <div class="col-md-7 p-4">
            <div class="d-flex justify-content-between align-items-center mb-3">
               <span class="badge badge-success px-3 py-2 text-uppercase">{{ selectedProduct.category }}</span>
               <span class="h4 text-success font-weight-bold mb-0">{{ selectedProduct.precio }}</span>
            </div>

            <p class="font-italic text-muted border-left pl-3 ml-1" style="border-width: 4px !important; border-color: #28a745 !important;">
              "{{ selectedProduct.ai_one_liner || (selectedProduct.ai_why_love && selectedProduct.ai_why_love[0]) || 'A great choice!' }}"
            </p>

            <!-- Why Love (List) -->
            <div v-if="selectedProduct.ai_why_love && selectedProduct.ai_why_love.length > 0" class="mb-4">
               <h6 class="font-weight-bold text-dark"><b-icon-heart-fill variant="danger" class="mr-2"></b-icon-heart-fill>Why We Love It</h6>
               <ul class="list-unstyled pl-2">
                 <li v-for="(reason, idx) in selectedProduct.ai_why_love" :key="idx" class="mb-2 d-flex align-items-start">
                   <b-icon-check-circle-fill variant="success" class="mr-2 mt-1 flex-shrink-0"></b-icon-check-circle-fill>
                   <span class="text-brand small">{{ reason }}</span>
                 </li>
               </ul>
            </div>

            <!-- Ideal Recipient -->
            <div v-if="selectedProduct.ai_ideal_recipient" class="mb-3">
               <h6 class="font-weight-bold text-dark"><b-icon-gift-fill variant="primary" class="mr-2"></b-icon-gift-fill>Perfect Gift For</h6>
               <p class="text-brand small pl-4">{{ selectedProduct.ai_ideal_recipient }}</p>
            </div>

            <!-- User Story -->
            <div v-if="selectedProduct.ai_user_story" class="mb-4 bg-light p-3 rounded">
               <h6 class="font-weight-bold text-dark mb-2"><b-icon-chat-quote-fill variant="secondary" class="mr-2"></b-icon-chat-quote-fill>User Experience</h6>
               <p class="text-brand small mb-0 font-italic">"{{ selectedProduct.ai_user_story }}"</p>
            </div>

            <b-button block variant="success" size="lg" @click="handleBuy(selectedProduct)" class="mt-4 font-weight-bold shadow-sm">
              View Deal <b-icon-box-arrow-up-right class="ml-2"></b-icon-box-arrow-up-right>
            </b-button>
            <p class="small text-muted text-center mt-2 mb-0">Affiliate link. Amazon purchases are not eligible for CashBoomerang user cash back.</p>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import firebase from 'firebase/app'
import 'firebase/firestore'
import { db } from '../main'
import { buildAffiliateUrl, isCashbackEligible } from '../services/affiliateLinks'
import { trackAffiliateClick } from '../services/analytics'

export default {
  name: 'Home',
  data() {
    return {
      products: [],
      loading: true,
      errorMessage: '',
      searchQuery: '',
      categories: ['Kitchen', 'Beauty', 'Fitness', 'Tech', '3c'],
      selectedProduct: null
    }
  },
  computed: {
    filteredProducts() {
      if (!this.searchQuery) {
        return this.products;
      }
      const query = this.searchQuery.toLowerCase();
      return this.products.filter(p => 
        p.titulo?.toLowerCase().includes(query) ||
        p.category?.toLowerCase().includes(query)
      );
    }
  },
  async mounted() {
    this.fetchProducts();
    
    // Listen for search events
    this.$root.$on('search-products', (query) => {
      this.searchQuery = query;
    });
  },
  methods: {
    async fetchProducts(retryCount = 0) {
      const MAX_RETRIES = 2;
      const TIMEOUT_MS = 30000; // 30 seconds
      
      console.log(`Starting fetchProducts... (Attempt ${retryCount + 1}/${MAX_RETRIES + 1})`);
      this.loading = true;
      this.errorMessage = '';
      
      // Timeout promise
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timed out after 30s')), TIMEOUT_MS)
      );

      try {
        console.log("Requesting Firestore from categories:", this.categories);
        
        const source = this.products.length > 0 ? 'cache' : 'default';
        const promises = this.categories.map(category => 
          db.collection(category).limit(12).get({ source })
        );
        
        const snapshots = await Promise.race([
          Promise.all(promises),
          timeoutPromise
        ]);
        
        console.log("Snapshots received:", snapshots.length);

        const allProducts = [];
        snapshots.forEach((snapshot, index) => {
          console.log(`  ${this.categories[index]}: ${snapshot.size} products`);
          snapshot.docs.forEach(doc => {
            const data = doc.data();
            allProducts.push({
              id: doc.id,
              ...data,
              category: this.categories[index]
            });
          });
        });
        
        console.log("Total products fetched:", allProducts.length);

        if (allProducts.length === 0) {
          console.warn("No products found in any category.");
          this.products = [];
          return;
        }

        // Sort by timestamp descending (Newest first)
        this.products = allProducts.sort((a, b) => {
          try {
            const tA = parseFloat(a.timestamp) || 0;
            const tB = parseFloat(b.timestamp) || 0;
            return tB - tA; 
          } catch (e) {
            return 0;
          }
        });
        console.log("✅ Loaded Products:", this.products.length);
        if (this.products.length > 0) {
          console.log("📦 First Product:", this.products[0]);
        }

      } catch (error) {
        console.error(`Fetch Error (Attempt ${retryCount + 1}):`, error);
        
        // Retry logic
        if (retryCount < MAX_RETRIES) {
          console.log(`Retrying in 2 seconds... (${retryCount + 1}/${MAX_RETRIES})`);
          this.errorMessage = `Loading failed, retrying... (${retryCount + 1}/${MAX_RETRIES})`;
          await new Promise(resolve => setTimeout(resolve, 2000));
          return this.fetchProducts(retryCount + 1);
        }
        
        // Final failure
        this.errorMessage = 'Failed to load data after multiple attempts. Please refresh the page.';
      } finally {
        console.log("Loading flag reset.");
        this.loading = false;
      }
    },
    getProxiedImage(imageUrl) {
      if (!imageUrl) {
        console.warn('⚠️ No image URL provided');
        return '';
      }
      
      // If it's a Firebase Storage URL, return directly (no proxy needed)
      if (imageUrl.includes('firebasestorage.googleapis.com') || 
          imageUrl.includes('firebasestorage.app')) {
        console.log('✅ Firebase Storage URL:', imageUrl);
        return imageUrl;
      }
      
      // For Amazon or other URLs, use proxy to bypass referrer restrictions
      const proxiedUrl = `https://images.weserv.nl/?url=${encodeURIComponent(imageUrl)}`;
      console.log('🔄 Proxied URL:', proxiedUrl);
      return proxiedUrl;
    },
    handleImageError(event) {
      // 如果代理也失败，显示占位图
      event.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23f0f0f0" width="200" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
    },
    async handleBuy(product) {
      if (!product.link) return;

      const targetUrl = buildAffiliateUrl(product.link);
      const user = this.$store.state.user;
      const outboundWindow = window.open('about:blank', '_blank');
      if (outboundWindow) outboundWindow.opener = null;

      try {
        if (user) {
          await db.collection('Clicks').add({
            userId: user.uid,
            productId: product.id,
            productName: product.titulo,
            originalLink: targetUrl,
            affiliateNetwork: product.affiliateNetwork || (targetUrl.includes('amazon.') ? 'amazon_associates' : 'unknown'),
            cashbackEligible: isCashbackEligible(product),
            price: product.precio,
            timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            status: 'clicked'
          });
        }

        trackAffiliateClick(product, targetUrl, 'home_product_card');

      } catch (error) {
        console.error("Click Tracking Failed:", error);
        trackAffiliateClick(product, targetUrl, 'home_product_card');
      } finally {
        if (outboundWindow) {
          outboundWindow.location.replace(targetUrl);
        } else {
          window.location.assign(targetUrl);
        }
      }
    },
    isCashbackEligible(product) {
      return isCashbackEligible(product);
    },
    goToDetail(product) {
      this.selectedProduct = product;
      this.$bvModal.show('product-modal');
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Google+Sans:wght@400;500;700&family=Product+Sans:wght@400;500&display=swap');

* {
  box-sizing: border-box;
}

.google-home {
  min-height: 100vh;
  background: #fff;
  font-family: 'Google Sans', 'Product Sans', Roboto, Arial, sans-serif;
}

/* Hero Section */
.hero {
  text-align: center;
  padding: 80px 24px 64px;
  background: #fff;
}

.hero-title {
  font-size: 48px;
  font-weight: 400;
  letter-spacing: -0.5px;
  color: #202124;
  margin: 0 0 16px;
}

.hero-subtitle {
  font-size: 18px;
  font-weight: 400;
  color: #5f6368;
  margin: 0;
}

.hero-actions { margin-top: 28px; }
.trust-strip { background: #eff8f1; border-top: 1px solid #dfece2; border-bottom: 1px solid #dfece2; padding: 56px 24px; }
.trust-content { max-width: 1100px; margin: 0 auto; text-align: center; }
.trust-content h2 { font-size: 32px; font-weight: 700; color: #203126; }
.trust-content > p:not(.section-kicker) { max-width: 820px; margin: 0 auto 28px; color: #536258; line-height: 1.7; }
.section-kicker { color: #198754; font-size: 12px; font-weight: 700; letter-spacing: .14em; }
.trust-points { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; text-align: left; }
.trust-points div { background: #fff; border: 1px solid #dbe8de; border-radius: 14px; padding: 20px; }
.trust-points strong, .trust-points span { display: block; }
.trust-points span { color: #637067; font-size: 14px; margin-top: 6px; line-height: 1.5; }
.content-heading { display: flex; justify-content: space-between; gap: 30px; align-items: flex-end; padding: 56px 0 30px; }
.content-heading h2 { font-weight: 700; color: #202124; margin: 0; }
.affiliate-note { max-width: 520px; color: #667169; font-size: 13px; line-height: 1.5; margin: 0; }
.eligibility-badge { display: inline-block; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; padding: 5px 8px; border-radius: 999px; margin-left: 5px; }
.eligibility-badge.eligible { background: #dff4e4; color: #146c36; }
.eligibility-badge.affiliate { background: #f1f3f4; color: #5f6368; }

/* Content Wrapper */
.content-wrapper {
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 24px 80px;
}

@media (max-width: 768px) {
  .trust-points { grid-template-columns: 1fr; }
  .content-heading { align-items: start; flex-direction: column; }
  .hero-actions .btn { display: block; width: 100%; margin: 10px 0 0 !important; }
}

/* Loading State */
.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f0f0f0;
  border-top-color: #1a73e8;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Error State */
.error-state {
  text-align: center;
  padding: 80px 24px;
}

.error-message {
  font-size: 16px;
  color: #d93025;
  margin: 0 0 8px;
}

.error-hint {
  font-size: 14px;
  color: #5f6368;
  margin: 0;
}

/* Product Grid */
.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
  margin-top: 8px;
}

/* Product Card */
.product-card {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  border: 1px solid #dadce0;
}

.product-card:hover {
  box-shadow: 0 1px 2px 0 rgba(60,64,67,0.3), 0 2px 6px 2px rgba(60,64,67,0.15);
  transform: translateY(-2px);
}

.product-image-wrapper {
  position: relative;
  width: 100%;
  padding-top: 75%; /* 4:3 Aspect Ratio */
  background: #f8f9fa;
  overflow: hidden;
}

.product-image {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-width: 85%;
  max-height: 85%;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.product-card:hover .product-image {
  transform: translate(-50%, -50%) scale(1.05);
}

.product-info {
  padding: 20px;
}

.product-category {
  display: inline-block;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #5f6368;
  margin-bottom: 8px;
}

.product-name {
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #202124;
  margin: 0 0 16px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 48px;
}

.product-description {
  font-size: 13px;
  line-height: 1.5;
  color: #5f6368;
  margin: 0 0 16px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-style: italic;
  background: #f8f9fa;
  padding: 8px;
  border-radius: 4px;
}

.product-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
}

.product-price {
  font-size: 18px;
  font-weight: 500;
  color: #202124;
}

.buy-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 9px 24px;
  background: #66bb6a;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  border-radius: 4px;
  transition: all 0.2s;
  border: none;
  cursor: pointer;
}

.buy-button:hover {
  background: #57a05b;
  box-shadow: 0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15);
  color: #fff;
  text-decoration: none;
}

/* Details Modal Styles */
.detail-image {
  max-height: 400px;
  object-fit: contain;
}

.close-btn {
  position: absolute;
  right: 15px;
  top: 15px;
  color: #5f6368;
  z-index: 10;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 120px 24px;
}

.empty-icon {
  margin-bottom: 24px;
  opacity: 0.5;
}

.empty-message {
  font-size: 22px;
  font-weight: 400;
  color: #202124;
  margin: 0 0 8px;
}

.empty-hint {
  font-size: 14px;
  color: #28a745;
  margin: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .hero-title {
    font-size: 36px;
  }
  
  .hero-subtitle {
    font-size: 16px;
  }
  
  .product-grid {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 16px;
  }
  
  .detail-image {
    max-height: 250px;
  }
}

@media (max-width: 480px) {
  .hero {
    padding: 48px 16px 40px;
  }
  
  .content-wrapper {
    padding: 0 16px 48px;
  }
  
  .product-grid {
    grid-template-columns: 1fr;
  }
}
.text-brand {
  color: #2e7d32 !important; /* Dark Green */
  font-weight: 500;
}
</style>
